import { useState, useCallback, useContext, useEffect } from "react";
import { REQUEST_STATUS, RequestStatus } from "../../../shared/consts";
import { topN } from "../consts";
import { fetchWordSuggestions } from "../word-suggestion-service";
import {
  promptValidForSuggestions,
  getFeDisplaySuggestion,
  replaceLastWord,
} from "../utils";
import { Suggestion, SuggestionType } from "../types";
import { UrlParamsContext } from "../../../app/context/url-params-context";

export const useWordSuggestionService = (
  input: string,
  setInput: (s: string) => void
) => {
  const { functionKey } = useContext(UrlParamsContext);
  const [displayAllSuggestions, setDisplayAllSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [inputKeywords, setInputKeywords] = useState<string[]>([]);
  const [feToBeTermsDict, setFeToBeTermsDict] = useState<
    Record<string, string>
  >({});

  const [feToTypeDict, setFeToTypeDict] = useState<
    Record<string, SuggestionType | undefined>
  >({});
  const [cursorPosition, setCursorPosition] = useState(0);
  const [wordSuggestionsRequestStatus, setWordSuggestionsRequestStatus] =
    useState<RequestStatus>(REQUEST_STATUS.IDLE);
  const [lastWord, setLastWord] = useState("");

  const handleSuggestionClick = (suggestion: Suggestion) => {
    const feSuggestion = getFeDisplaySuggestion(suggestion);
    const updatedText = replaceLastWord(feSuggestion, input, cursorPosition);
    setInput(updatedText);
    setInputKeywords((inputKeywords) => [...inputKeywords, feSuggestion]);
    setFeToBeTermsDict((prevDict) => ({
      ...prevDict,
      [feSuggestion]: suggestion.be_display,
    }));
    setFeToTypeDict((prevDict) => ({
      ...prevDict,
      [feSuggestion]: suggestion.type,
    }));
    setSuggestions([]);
  };
  const resetWordSuggestions = () => {
    setSuggestions([]);
    setInputKeywords([]);
    setFeToBeTermsDict({});
    setFeToTypeDict({});
  };

  const handleUserInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newInput = e.target.value;
      setInput(newInput);
      const cursorPosition = e.target.selectionStart ?? newInput.length;
      setCursorPosition(cursorPosition);
      const leftPart = newInput.slice(0, cursorPosition);
      const leftWordMatch = leftPart.match(/(\S+)?$/); // Word to the left of the cursor
      const leftWord = leftWordMatch ? leftWordMatch[0] : "";
      setLastWord(leftWord.trim());

      if (promptValidForSuggestions(newInput, cursorPosition)) {
        const lastTypedWord = leftWord.trim().toLowerCase();
        try {
          setWordSuggestionsRequestStatus(REQUEST_STATUS.LOADING);
          const { suggestions: newSuggestions } = await fetchWordSuggestions(
            lastTypedWord,
            topN,
            displayAllSuggestions,
            functionKey
          );
          setSuggestions(newSuggestions);
          setWordSuggestionsRequestStatus(REQUEST_STATUS.SUCCESS);
        } catch (error: any) {
          console.error(error.message);
          setWordSuggestionsRequestStatus(REQUEST_STATUS.FAILURE);
        }
      } else {
        setSuggestions([]);
      }
    },
    [displayAllSuggestions, functionKey]
  );
  useEffect(() => {
    if (
      !promptValidForSuggestions(input, cursorPosition) &&
      suggestions.length !== 0
    ) {
      setSuggestions([]);
    }
  }, [input, suggestions, cursorPosition]);

  return {
    suggestions,
    setSuggestions,
    inputKeywords,
    setInputKeywords,
    handleSuggestionClick,
    displayAllSuggestions,
    setDisplayAllSuggestions,
    resetWordSuggestions,
    handleUserInputChange,
    feToBeTermsDict,
    wordSuggestionsRequestStatus,
    feToTypeDict,
    lastWord,
  };
};
